/**
 * Picks the right description/benefits pair for the active i18n language.
 *
 * Mirrors selectPackageCopy in apps/web/app/(tenant)/book/_lib/format-package-label.ts
 * so both surfaces resolve localized copy the same way.
 *
 * Tenants author the primary copy in `description` / `benefits` (typically
 * English) and the optional secondary-language copy in `description_alt` /
 * `benefits_alt` (typically Arabic, but the schema is locale-agnostic).
 *
 * When the active language is the primary (`en`), we always render the
 * primary columns. For any other language we try `_alt` first and fall
 * back to the primary if the tenant hasn't authored the translation.
 */
export function selectMembershipPlanCopy(
  plan: {
    description?: string | null;
    description_alt?: string | null;
    benefits?: string[] | null;
    benefits_alt?: string[] | null;
  },
  language: string,
  primaryLanguage: string = 'en',
): { description: string | null; benefits: string[] } {
  const isAlt = language !== primaryLanguage;
  const alt = (plan.description_alt ?? '').trim();

  // Strip blank/whitespace-only entries from both lists — same hygiene
  // as selectPackageCopy. Admin drawer's onBlur already cleans them on
  // save, but legacy rows / service-layer writes can bypass that.
  const altBenefits = (plan.benefits_alt ?? []).filter((b) => b.trim() !== '');
  const primaryBenefits = (plan.benefits ?? []).filter((b) => b.trim() !== '');

  return {
    description: isAlt
      ? alt.length > 0
        ? alt
        : plan.description ?? null
      : plan.description ?? null,
    benefits:
      isAlt && altBenefits.length > 0 ? altBenefits : primaryBenefits,
  };
}
